import React, { useEffect, useId, useMemo, useState } from 'react';
import './HeroWave.css';

const VB_W = 1440;
const VB_H = 640;
const CX = 720;
const CY = 320;
const TOP = 12;
const BOT = 628;
const CP1X = 210;
const CP1Y = 200;
const CP2X = 520;

const lerp = (a, b, t) => a + (b - a) * t;

const curve = (edgeY, cp1Y) =>
  `M 0 ${edgeY} C ${CP1X} ${cp1Y}, ${CP2X} ${CY}, ${CX} ${CY} S ${VB_W - CP1X} ${cp1Y}, ${VB_W} ${edgeY}`;

const buildPaths = () => {
  const steps = [0, 1 / 3, 2 / 3];
  const above = steps.map((t) => curve(lerp(TOP, CY, t), lerp(CP1Y, CY, t)));
  const straight = `M 0 ${CY} L ${VB_W} ${CY}`;
  const below = steps.map((t) =>
    curve(lerp(BOT, CY, t), lerp(2 * CY - CP1Y, CY, t))
  );
  return [...above, straight, ...below];
};

const HeroWave = () => {
  const rawId = useId();
  const uid = `heroWave-${rawId.replace(/:/g, '')}`;
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  const paths = useMemo(() => buildPaths(), []);

  return (
    <svg
      className="hero-wave"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient
          id={`${uid}-grad`}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2={VB_W}
          y2="0"
          spreadMethod="repeat"
        >
          <stop offset="0" stopColor="#046242" />
          <stop offset="0.18" stopColor="#BED347" />
          <stop offset="0.34" stopColor="#E4FD58" />
          <stop offset="0.5" stopColor="#F56116" />
          <stop offset="0.68" stopColor="#F6C531" />
          <stop offset="0.84" stopColor="#FFC10D" />
          <stop offset="1" stopColor="#046242" />
          {!reduceMotion && (
            <>
              <animate
                attributeName="x1"
                values={`${-VB_W};0`}
                dur="10s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="x2"
                values={`0;${VB_W}`}
                dur="10s"
                repeatCount="indefinite"
              />
            </>
          )}
        </linearGradient>
      </defs>
      {paths.map((d, index) => (
        <path
          key={index}
          className="hero-wave-line"
          d={d}
          stroke={`url(#${uid}-grad)`}
        />
      ))}
    </svg>
  );
};

export default HeroWave;
